import { useRef, useState } from "react";

import { ImageIcon, ImageOffIcon, UploadIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import { FileViewer } from "@/components/shared/file-viewer";

import { cn } from "@/lib/utils";
import { resolveMediaUrl } from "@/lib/media";
import { useUploadImage } from "@/services";

interface ImageUploaderProps {
  value?: string;
  onChange: (url: string | undefined) => void;
  /** Ko'ruvchi sarlavhasi va yuklash tugmasining nomi uchun. */
  label: string;
  className?: string;
}

/**
 * Rasm yuklovchi.
 *
 * Manzil hech qayerda MATN sifatida chiqmaydi — faqat rasmning o'zi va uni
 * to'liq ochadigan ICHKI ko'ruvchi. Xom MinIO havolasi ham, yangi tab ham
 * yo'q (mahsulot talabi).
 */
export const ImageUploader = ({ value, onChange, label, className }: ImageUploaderProps) => {
  const { t } = useTranslation();
  const upload = useUploadImage();
  const inputRef = useRef<HTMLInputElement>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="border-border bg-muted/30 flex aspect-video w-full max-w-sm items-center justify-center overflow-hidden rounded-xl border">
        {value ? (
          <img src={resolveMediaUrl(value)} alt="" className="size-full object-cover" />
        ) : (
          <span className="text-muted-foreground flex flex-col items-center gap-2 text-xs">
            <ImageOffIcon className="size-8" />
            {t("UPLOAD.NO_IMAGE")}
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        aria-label={label}
        onChange={event => {
          const file = event.target.files?.[0];
          if (!file) return;
          upload.mutate(file, { onSuccess: res => onChange(res.data.url) });
          event.target.value = "";
        }}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          loading={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          <UploadIcon className="size-4" />
          {value ? t("UPLOAD.REPLACE") : t("UPLOAD.UPLOAD")}
        </Button>

        {value && (
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => setViewerOpen(true)}>
              <ImageIcon className="size-4" />
              {t("UPLOAD.PREVIEW")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive"
              onClick={() => onChange(undefined)}
            >
              <XIcon className="size-4" />
              {t("UPLOAD.REMOVE")}
            </Button>
          </>
        )}
      </div>

      <FileViewer open={viewerOpen} onOpenChange={setViewerOpen} url={value} title={label} />
    </div>
  );
};
